/* Admin Center — shared style consts + drag-reorder hook + skeleton row (extracted from AdminPage.tsx) */

import React from 'react';
import { rem } from '../../lib/rem';

const inputStyle: React.CSSProperties = {
  padding: `${rem(7)} ${rem(10)}`,
  borderRadius: rem(5),
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  width: '100%',
};
const btnPrimary: React.CSSProperties = {
  padding: `${rem(7)} var(--space-4)`,
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  padding: `${rem(5)} ${rem(10)}`,
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'transparent',
  color: 'var(--dg)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  cursor: 'pointer',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  borderBottom: '1px solid var(--bd)',
  background: 'var(--sa)',
};
const tdStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderBottom: '1px solid var(--bd)',
  fontSize: 'var(--font-size-base)',
};

/* ─── Drag-to-reorder helpers ───
 * Every admin lookup list uses the same pattern:
 *   1. User drags a row by its handle.
 *   2. On drop, compute the new order, write `sortOrder = index` to every row
 *      that actually moved, persist via the table's service.save.
 * The hook keeps the drag state local to each table instance so two tables on
 * screen can't fight over it. The drag handle cell is a small vertical-dots
 * glyph the user can grab; the row is the drop target. */

interface DragHandlers<T extends { id: string }> {
  dragIdx: number | null;
  overIdx: number | null;
  rowProps: (i: number) => React.HTMLAttributes<HTMLElement>;
  handleProps: () => React.HTMLAttributes<HTMLElement>;
}

function useDragReorder<T extends { id: string; sortOrder?: number }>(
  items: T[],
  onReorder: (reordered: T[]) => Promise<void> | void
): DragHandlers<T> {
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [overIdx, setOverIdx] = React.useState<number | null>(null);

  async function commit(from: number, to: number) {
    setDragIdx(null);
    setOverIdx(null);
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const withOrder = next.map((it, i) => ({ ...it, sortOrder: i }));
    await onReorder(withOrder);
  }

  return {
    dragIdx,
    overIdx,
    rowProps: (i: number) => ({
      // The handle sets draggable=true so clicks elsewhere on the row don't start a drag.
      onDragOver: (e: React.DragEvent) => {
        if (dragIdx === null) return;
        e.preventDefault();
        if (overIdx !== i) setOverIdx(i);
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        if (dragIdx !== null) commit(dragIdx, i);
      },
      'data-drag-idx': i as any,
    }),
    handleProps: () => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        // `currentTarget` is the handle itself; walk up to the row to read its index.
        const row = (e.currentTarget as HTMLElement).closest('[data-drag-idx]');
        const idx = row ? parseInt(row.getAttribute('data-drag-idx') || '-1', 10) : -1;
        if (idx >= 0) setDragIdx(idx);
        e.dataTransfer.effectAllowed = 'move';
      },
      onDragEnd: () => {
        setDragIdx(null);
        setOverIdx(null);
      },
      style: { cursor: 'grab' } as React.CSSProperties,
    }),
  };
}

// The grip glyph used consistently across every drag handle.
const DRAG_HANDLE_STYLE: React.CSSProperties = {
  width: rem(18),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--tm)',
  fontSize: 'var(--font-size-lg)',
  lineHeight: 1,
  userSelect: 'none',
  flexShrink: 0,
};

/** Shared row-skeleton shown during a tab's first data load (skeletons over
 *  blocking spinners — DESIGN.md §4). Mirrors the row rhythm of the simple
 *  list tables this file renders. */
function RowSkeleton({ rows = 4, label }: { rows?: number; label: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        background: 'var(--sf)',
        borderRadius: rem(8),
        border: '1px solid var(--bd)',
        overflow: 'hidden',
        padding: 'var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 'var(--space-6)',
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            opacity: Math.max(0.3, 0.95 - i * 0.15),
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

export type { DragHandlers };
export {
  inputStyle,
  btnPrimary,
  btnDanger,
  thStyle,
  tdStyle,
  useDragReorder,
  DRAG_HANDLE_STYLE,
  RowSkeleton,
};
